/** admin/cohorts — HOOKS. */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { cohortsApi } from './api';
import { adminCohortsKeys } from './keys';

const invalidate = (qc: QueryClient) => qc.invalidateQueries({ queryKey: adminCohortsKeys.all() });

export function useAdminCohorts(enabled = true) {
  return useQuery({ queryKey: adminCohortsKeys.lists(), queryFn: () => cohortsApi.list(), enabled });
}

export function useAdminCohort(id: string | null) {
  return useQuery({
    queryKey: adminCohortsKeys.detail(id ?? 'none'),
    queryFn: () => cohortsApi.get(id!),
    enabled: !!id,
  });
}

export function useCreateCohort() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (payload: { name: string; description?: string }) => cohortsApi.create(payload), onSuccess: () => invalidate(qc) });
}

export function useUpdateCohort() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { id: string; payload: { name?: string; description?: string; isActive?: boolean } }) => cohortsApi.update(v.id, v.payload),
    onSuccess: () => invalidate(qc),
  });
}

export function useDeleteCohort() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (id: string) => cohortsApi.remove(id), onSuccess: () => invalidate(qc) });
}

export function useAddCohortStudents() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; studentIds: string[] }) => cohortsApi.addStudents(v.id, v.studentIds), onSuccess: () => invalidate(qc) });
}

export function useRemoveCohortStudents() {
  const qc = useQueryClient();
  return useMutation({ mutationFn: (v: { id: string; studentIds: string[] }) => cohortsApi.removeStudents(v.id, v.studentIds), onSuccess: () => invalidate(qc) });
}
